import { ReactNode } from "react";
import { NavLink } from "react-router";
import { ServiceKind } from "@bitory-gmbh/bsm";
import {
  ListRequest,
  OrderValues,
  useProcessesProcessListQuery,
} from "@/services/backend/processes/api/process-api";
import { Label } from "@/services/backend/processes/api/state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import {
  LastUpdatedDocument,
  ViewModel,
} from "@/services/backend/processes/types/process";
import { ProcessNumber } from "@/services/backend/numbers/components/process-number";
import { StateIcon } from "@/services/backend/processes/components/state/state-icon";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { BranchName } from "@/services/backend/company/components/branch-name";
import { formatDate } from "@/shared/lib/utilities/date";
import { ServiceKindFilter } from "@/services/backend/services/components/service-kind-filter";
import { BranchFilter } from "@/services/backend/company/components/branch-filter";
import { StateFilter } from "@/services/backend/processes/components/state/state-filter";
import { ResetFilters } from "@/shared/components/filter/reset-filters";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { ProcessKindFilter } from "@/services/backend/processes/components/process-kind-filter";
import { ServiceObjectFilter } from "@/services/backend/customers/components/service-object-filter";
import { ProcessOwningUserFilter } from "./process-owning-user-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { useDocumentPath } from "@/services/backend/documents/components/use-document-path";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { Button } from "@/shared/components/ui/button";

type RenderRowLink = (
  processId: string,
  serviceKind: ServiceKind,
  children: ReactNode,
) => ReactNode;

export function ProcessTable({
  request,
  onRequestChange,
  renderRowLink,
  hideBorder = false,
  hidePagination = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: RenderRowLink;
  hideBorder?: boolean;
  hidePagination?: boolean;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useProcessesProcessListQuery(request, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <ProcessTableHeader />
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={9} />}
            {error && <TableRtkErrorRow error={error} colSpan={9} />}
            {emptyList && <TableEmptyRow colSpan={9} />}
            {list &&
              list.items.map((process) => (
                <ProcessTableRow
                  key={process.id}
                  process={process}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <div className="flex items-center">
          <div className="pl-2 text-sm font-medium">
            {t(`Total: ${list.totalCount} Vorgänge`)}
          </div>
          <PaginationLimitOffset
            offset={request.offset ?? 0}
            limit={request.limit ?? 0}
            totalCount={list.totalCount}
            setLimitOffset={(limit, offset) => {
              onRequestChange({ ...request, limit, offset });
            }}
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}

function ProcessTableHeader() {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>{t("DL")}</TableHead>
        <TableHead>{t("Status")}</TableHead>
        <TableHead>{t("Nummer")}</TableHead>
        <TableHead>{t("Thema")}</TableHead>
        <TableHead>{t("Kunde")}</TableHead>
        <TableHead>{t("NL")}</TableHead>
        <TableHead>{t("Erstellt am")}</TableHead>
        <TableHead>{t("Bearbeitet am")}</TableHead>
        <TableHead>{t("Letztes bearbeitetes Dokument")}</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function ProcessTableRow({
  process,
  renderRowLink,
}: {
  process: ViewModel;
  renderRowLink: RenderRowLink;
}) {
  return (
    <TableRow key={process.id}>
      <TableCell className="pr-0">{process.serviceKind}</TableCell>
      <TableCell className="pr-0">
        <StateIcon label={process.state} className="ml-2" />
      </TableCell>
      <TableCell className="pr-0">
        <ProcessNumber processId={process.id} className="font-mono" />
      </TableCell>
      <TableCell className="py-0 pr-0">
        {renderRowLink(
          process.id,
          process.serviceKind,
          <span className="max-w-[350px] truncate">
            {process.topic === "" ? "- tbd -" : process.topic}
          </span>,
        )}
      </TableCell>
      <TableCell>
        <CustomerName customerId={process.customerId} />
      </TableCell>
      <TableCell>
        <BranchesCell process={process} />
      </TableCell>
      <TableCell>{formatDate(process.createdAt)}</TableCell>
      <TableCell>{formatDate(process.updatedAt)}</TableCell>
      <TableCell className="py-0 pr-0">
        {process.lastUpdatedDocument && (
          <LastUpdatedDocumentCell
            lastUpdatedDocument={process.lastUpdatedDocument}
            serviceKind={process.serviceKind}
            processId={process.id}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function LastUpdatedDocumentCell({
  lastUpdatedDocument,
  serviceKind,
  processId,
}: {
  lastUpdatedDocument: LastUpdatedDocument;
  serviceKind: ServiceKind;
  processId: string;
}) {
  const { path } = useDocumentPath({
    documentId: lastUpdatedDocument.documentId,
    documentKind: lastUpdatedDocument.documentKind,
    serviceKind: serviceKind as GSMServiceKind,
    processId,
  });

  const label = `${t(lastUpdatedDocument.documentKind)} am ${formatDate(
    lastUpdatedDocument.updatedAt,
  )}`;

  if (!path) {
    return label;
  }

  return (
    <Button
      asChild
      variant="link"
      size="sm"
      className="h-auto p-0 whitespace-normal"
    >
      <NavLink to={path}>{label}</NavLink>
    </Button>
  );
}

function BranchesCell({ process }: { process: ViewModel }) {
  if (process.branches.length === 0) {
    return "-";
  }

  return (
    <span className="flex gap-0.5">
      {process.branches.map((id, idx) => {
        if (process.branches.length > 1 && idx < process.branches.length - 1) {
          return (
            <span key={id}>
              <BranchName key={id} branchId={id} />
              <span>, </span>
            </span>
          );
        }

        return <BranchName key={id} branchId={id} />;
      })}
    </span>
  );
}

export function ProcessTableFilters({
  request,
  onRequestChange,
  defaultRequest,
  hideCompanyFilter = false,
  hideBranchFilter = false,
  hideCustomerFilter = false,
  hideServiceObjectFilter = false,
  hideServiceKindFilter = false,
  hideStateFilter = false,
  hideProcessKindFilter = false,
  hideProcessOwningUserFilter = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  defaultRequest: ListRequest;
  hideCompanyFilter?: boolean;
  hideBranchFilter?: boolean;
  hideCustomerFilter?: boolean;
  hideServiceObjectFilter?: boolean;
  hideServiceKindFilter?: boolean;
  hideStateFilter?: boolean;
  hideProcessKindFilter?: boolean;
  hideProcessOwningUserFilter?: boolean;
}) {
  return (
    <div className="flex space-x-2">
      <CompanyFilter
        filter={request.company}
        onFilterChange={(company) => onRequestChange({ ...request, company })}
        hidden={hideCompanyFilter}
      />
      <ServiceKindFilter
        filter={request.serviceKind}
        onFilterChange={(serviceKind) =>
          onRequestChange({ ...request, serviceKind })
        }
        hidden={hideServiceKindFilter}
      />
      <StateFilter
        filter={request.state}
        onFilterChange={(state) => onRequestChange({ ...request, state })}
        hidden={hideStateFilter}
      />
      <CustomerFilter
        filter={request.customer}
        onFilterChange={(customer) => onRequestChange({ ...request, customer })}
        hidden={hideCustomerFilter}
      />
      <ServiceObjectFilter
        filter={request.serviceObject}
        onFilterChange={(serviceObject) =>
          onRequestChange({ ...request, serviceObject })
        }
        customerIds={
          request.customer?.active ? request.customer.values : undefined
        }
        hidden={hideServiceObjectFilter}
      />
      <BranchFilter
        filter={request.branch}
        onFilterChange={(branch) => onRequestChange({ ...request, branch })}
        hidden={hideBranchFilter}
      />
      <ProcessKindFilter
        filter={request.kind}
        onFilterChange={(kind) => onRequestChange({ ...request, kind })}
        hidden={hideProcessKindFilter}
      />
      <ProcessOwningUserFilter
        filter={request.owningUser}
        onFilterChange={(owningUser) =>
          onRequestChange({ ...request, owningUser })
        }
        hidden={hideProcessOwningUserFilter}
      />
      <OrderBySelect<OrderValues>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Dienstleistung"), value: "serviceKind" },
          { label: t("Thema"), value: "topic" },
          { label: t("Status"), value: "state" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Bearbeitet am"), value: "updatedAt" },
          { label: t("Letzte Aktivität"), value: "lastActivityAt" },
        ]}
      />
      <ResetFilters onClick={() => onRequestChange(defaultRequest)} />
    </div>
  );
}

export function useDefaultProcessListRequest(): ListRequest {
  return {
    kind: {
      active: false,
      values: [],
    },
    serviceKind: {
      active: false,
      values: [],
    },
    topic: {
      active: false,
      values: [],
    },
    customer: {
      active: false,
      values: [],
    },
    company: {
      active: false,
      values: [],
    },
    branch: {
      active: false,
      values: [],
    },
    owningUser: {
      active: false,
      values: [],
    },
    state: {
      active: true,
      values: [Label.Running],
    },
    orderBy: [{ field: "lastActivityAt" as OrderValues, dsc: true }],
    limit: 50,
    offset: 0,
  };
}
